import React, {useState, useMemo, useCallback} from "react";
import {TreeView, TreeItem} from "@mui/lab";

import {Checkbox, Typography} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

export default function RevisionTree({task, checkedIds, setCheckedIds}) {
    const nodes = useMemo(() => {
        const wrap = (n) => ({
            value: n.legalDocumentId ?? n.id,
            labelText: n.legalDocumentName ?? n.name,
            children: (n.toCElements || []).map(wrap),
        });
        return [wrap(task)];
    }, [task]);

    const getLeafIds = useCallback((node) => {
        if (node.children.length === 0) return [node.value];
        return node.children.flatMap(getLeafIds);
    }, []);

    const handleCheck = useCallback(
        (node) => {
            const leafIds = getLeafIds(node);
            const allChecked = leafIds.every((id) => checkedIds.includes(id));
            if (allChecked) {
                // remove them
                setCheckedIds((prev) => prev.filter((id) => !leafIds.includes(id)));
            } else {
                // add those not yet in
                setCheckedIds((prev) => [...prev, ...leafIds.filter((id) => !prev.includes(id))]);
            }
        },
        [checkedIds, getLeafIds, setCheckedIds]
    );

    const [expanded, setExpanded] = useState([task.legalDocumentId]);

    const renderTree = (node) => {
        const leafIds = getLeafIds(node);
        const allChecked = leafIds.every((id) => checkedIds.includes(id));
        const someChecked = leafIds.some((id) => checkedIds.includes(id));

        return (
            <TreeItem
                key={node.value}
                nodeId={String(node.value)}
                label={
                    <div style={{display: "flex", alignItems: "center", gap: 4}}>
                        <Checkbox
                            size="small"
                            checked={allChecked}
                            indeterminate={!allChecked && someChecked}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleCheck(node);
                            }}
                        />
                        <Typography variant="body2" sx={{fontWeight: 500}}>
                            {node.labelText}
                        </Typography>
                    </div>
                }
            >
                {node.children.map((child) => renderTree(child))}
            </TreeItem>
        );
    };

    return (
        <TreeView
            defaultCollapseIcon={<ExpandMoreIcon/>}
            defaultExpandIcon={<ChevronRightIcon/>}
            expanded={expanded}
            onNodeToggle={(_, newExpanded) => setExpanded(newExpanded)}
            disableSelection
        >
            {nodes.map(renderTree)}
        </TreeView>
    );
}
